import { useSyncExternalStore } from "react";

/**
 * Display and read-aloud settings (docs/UX.md §1, §2 «Einstellungen»).
 *
 * They belong to the device, not the account: nothing here goes to the
 * server, so no new user attribute exists (CLAUDE.md, rule 2). A family
 * tablet shared by two children shares these too, which is fine for how big
 * the letters are.
 */
export type TextSize = "normal" | "large" | "xlarge";
export type SpeechRate = "normal" | "slow";

export interface Settings {
  textSize: TextSize;
  /** Atkinson Hyperlegible instead of the house sans. */
  readableFont: boolean;
  /** Read the foreign word aloud once per card, without a tap. */
  autoSpeak: boolean;
  speechRate: SpeechRate;
}

const KEY = "vf.settings";
const DEFAULTS: Settings = {
  textSize: "normal",
  readableFont: false,
  // Off by default: a class of 25 tablets reading aloud at once is not
  // practice, and everything has to work without sound (docs/UX.md §1).
  autoSpeak: false,
  speechRate: "normal",
};

/** Root font size; every rem-based size in the interface follows it. */
export const TEXT_SIZE_PERCENT: Record<TextSize, number> = {
  normal: 100,
  large: 115,
  xlarge: 130,
};

function load(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return {
      textSize: parsed.textSize && parsed.textSize in TEXT_SIZE_PERCENT ? parsed.textSize : DEFAULTS.textSize,
      readableFont: parsed.readableFont === true,
      autoSpeak: parsed.autoSpeak === true,
      speechRate: parsed.speechRate === "slow" ? "slow" : "normal",
    };
  } catch {
    return DEFAULTS;
  }
}

let current = load();
const listeners = new Set<() => void>();

/** Mirror the display settings onto <html>, where the CSS picks them up. */
export function applySettings(settings: Settings = current): void {
  const root = document.documentElement;
  root.style.fontSize = `${TEXT_SIZE_PERCENT[settings.textSize]}%`;
  root.toggleAttribute("data-readable-font", settings.readableFont);
}

export function getSettings(): Settings {
  return current;
}

export function updateSettings(patch: Partial<Settings>): void {
  current = { ...current, ...patch };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    // Private mode or full storage: the setting holds until the tab closes.
  }
  applySettings(current);
  for (const listener of listeners) listener();
}

export function useSettings(): Settings {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
  );
}
